import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  activateSimCardMsg,
  fieldCantBeEmptyMsg,
  invalidSIMCardNumberMsg,
  orderEsimMsg,
  orderNewSimCardMsg,
  simCardMsg,
  simCardOneSimPerSubscriptionMsg,
  t,
} from '../../../../common/i18n/index.js';
import { SelectRadio } from '../../../../common/react-hook-form/components/SelectRadio.js';
import { SimOption, getLuhnRemainder } from '../../../../common/utils/simCardUtils.js';
import { SIM_CARD_REGEX } from '../../../../common/utils/validationUtils.js';
import { SimType } from '../../../../generated/api/simType.js';
import { LabelWithTooltip } from '../../../LabelWithTooltip/LabelWithTooltip.js';
import { SimCardNumberInput } from '../../../SimCardNumberInput/SimCardNumberInput.js';
import type { SelectRadioItem } from '../../../../common/react-hook-form/components/SelectRadio.js';
import type { SimCardConfiguration } from '../../../../common/types/subscription.js';

import './SimCardDuringNewSubscriptionOrder.scss';

export const ACTIVATE_EXISTING = 'ACTIVATE_EXISTING';

const isInvalidSimCardNumber = (simCardNumber: string): boolean =>
  !simCardNumber.match(new RegExp(SIM_CARD_REGEX)) || getLuhnRemainder(simCardNumber) !== 0;

export interface PlaceOrderSimCardProps {
  cartItemInstanceId: string;
  onClickSimCardNumberHelp: () => void;
  simCardConfiguration: SimCardConfiguration;
  simOptions: SimOption[];
  /** SIM card numbers used across all order items, for duplicate detection */
  simCardNumbers: string[];
  /** Backend validation error from useSubscriptionConfigForm */
  backendError?: string;
  showESIM?: boolean;
  showPSIM?: boolean;
  showActiveSim?: boolean;
}

export const PlaceOrderSimCard = ({
  cartItemInstanceId,
  onClickSimCardNumberHelp,
  simCardConfiguration,
  simOptions,
  simCardNumbers,
  backendError,
  showESIM = true,
  showPSIM = true,
  showActiveSim = true,
}: PlaceOrderSimCardProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { isReady },
  } = useFormContext();

  const selectedType = watch(`configuration.${cartItemInstanceId}.simCardConfiguration.simType`);
  const defaultValue =
    simCardConfiguration.simSelection === ACTIVATE_EXISTING ? ACTIVATE_EXISTING : simCardConfiguration?.simType;

  useEffect(() => {
    if (isReady && selectedType === undefined && defaultValue !== undefined) {
      setValue(`configuration.${cartItemInstanceId}.simCardConfiguration.simType`, defaultValue);
    }
  }, [isReady, cartItemInstanceId, selectedType, defaultValue, setValue]);

  // Watch the SIM number to include in the validate closure
  const simCardNumber = useWatch({
    name: `configuration.${cartItemInstanceId}.simCardConfiguration.simCardNumber`,
  });

  const simItems: SelectRadioItem[] = [];
  if (simOptions.includes(SimOption.ORDER_NEW_PHYSICAL_MINI_MICRO)) {
    simItems.push({
      label: t.A7CJ('Order new (Mini + Micro)'),
      value: SimType.PHYSICAL_MINI_MICRO,
    });
  }
  if (simOptions.includes(SimOption.ORDER_NEW_PHYSICAL_NANO)) {
    simItems.push({
      label: t.A7CK('Order new (Nano)'),
      value: SimType.PHYSICAL_NANO,
    });
  }
  if (simOptions.includes(SimOption.ORDER_NEW_PHYSICAL) && showPSIM) {
    simItems.push({
      label: t.QYXA(orderNewSimCardMsg),
      value: SimType.PHYSICAL,
    });
  }
  if (simOptions.includes(SimOption.ORDER_NEW_ESIM) && showESIM) {
    simItems.push({
      label: t.DD3Q(orderEsimMsg),
      value: SimType.ESIM,
    });
  }
  if (simOptions.includes(SimOption.ACTIVATE_EXISTING_PHYSICAL) && showActiveSim) {
    simItems.push({
      label: t.M1E7(activateSimCardMsg),
      value: ACTIVATE_EXISTING,
    });
  }

  const validateSimCard = (value: string) => {
    if (value && isInvalidSimCardNumber(value)) {
      return t.SDXB(invalidSIMCardNumberMsg);
    }

    // Backend validation error from the hook (e.g. SIM already in use)
    if (backendError && value === simCardNumber) {
      return backendError;
    }

    if (simCardNumbers.filter(otherSimCardNumber => otherSimCardNumber === value).length > 1) {
      return t.RFH9(simCardOneSimPerSubscriptionMsg);
    }

    return undefined;
  };

  return (
    <div
      id={`simCardConfiguration-${cartItemInstanceId}`}
      className="of-sim-card-selection of-sim-card-selection-0"
      role="group"
    >
      <LabelWithTooltip className="ea-h4" labelText={t.PIZC(simCardMsg)}>
        {undefined}
      </LabelWithTooltip>
      <SelectRadio
        name={`configuration.${cartItemInstanceId}.simCardConfiguration.simType`}
        defaultValue={defaultValue}
        items={simItems}
      />
      {selectedType === SimType.ESIM && (
        <div className="of-sim-card-selection__esim-info">
          <p>
            {t.MF9P(
              'eSIM is a digital SIM card. At the moment eSIM compatible devices are the newest iPhones, iPads and Samsung Galaxy Watches.'
            )}
          </p>
          <a href="https://elisa.fi/esim/" target="_blank" rel="noopener noreferrer">
            {t.MC5L('eSIM compatible devices')}
          </a>
        </div>
      )}
      {selectedType === ACTIVATE_EXISTING && (
        <Controller
          name={`configuration.${cartItemInstanceId}.simCardConfiguration.simCardNumber`}
          control={control}
          shouldUnregister={true}
          defaultValue={simCardConfiguration.simCardNumber || ''}
          rules={{
            required: { value: true, message: t.VPVR(fieldCantBeEmptyMsg) },
            validate: validateSimCard,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <SimCardNumberInput
              error={error?.message}
              onChange={onChange}
              value={value}
              onAppendedButtonClick={onClickSimCardNumberHelp}
            />
          )}
        />
      )}
    </div>
  );
};
